import './app.scss';

import { Box, Button, Stack, Typography } from '@mui/material';
import {
  BrokerAgencyDetailAgents,
  BrokerAgencyDetailOverview,
  BrokerageContainer,
  CircularLoader,
  ClaimDetails,
  ClaimDiary,
  ClaimDocumentDetailNote,
  ClaimEntityDetailContact,
  ClaimEntityDetailCoverageLetter,
  ClaimEntityDetailFinancials,
  ClaimEntityDetailNote,
  ClaimEntityDetailOverview,
  ClaimHistoryLogDetail,
  ClaimReportDetail,
  CreateBrokerContainer,
  ErrorPageLayout,
  InsuredDocumentDetails,
  InsuredEntityDetailOverview,
  InsuredPoliciesDetails,
  PolicyDetailOverview,
  QuotesDetailOverview,
  QuotesDetailPolicies,
  QuotesDetailSubmissions,
  UnderwritingSubjectivitiesPage,
  UnderwritingSubmissionDetailOverview,
  UnderwritingSubmissionDetailQuestionnaire,
  useAuthTokenRefresh,
  useIdleLogout,
} from '@converge/ui';
import { InsuredEntityDetailReports } from '../../../../libs/ui/src/domain/entity/insured/reports/insured-entity.detail.reports';
import {
  Link,
  Navigate,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';
import { Suspense, lazy } from 'react';

import AppLayout from '../pages/layout/app-layout';
import BrokerDetailPage from 'src/pages/entity/producers/broker-detail-page';
import BrokerageDetailPage from 'src/pages/entity/producers/brokerage-detail-page';
import ClaimEntityPage from 'src/pages/entity/claims/claim-entity-page';
import { ErrorBoundaryPage } from 'src/pages/error/error-boundary.page';
import { ErrorPage } from 'src/pages/error.page';
import Login from '../pages/login/login';
import PolicyDetailPage from 'src/pages/entity/policies/policies-entity-detail-page';
import ProducersPage from 'src/pages/entity/producers/producers-page';
import QuotesEntityDetailPage from 'src/pages/entity/quotes/quotes-entity-detail-page';
import { RaterMode } from '@converge/graphql';
import { UnderwritingSubmissionDetailPageSlug } from '@converge/data';
import { useHealthCheckQuery } from '@converge/graphql';
import { usePromptNavigationListener } from 'src/hooks';

const UnderwritingDashboardPage = lazy(
  () => import('../pages/underwriting/underwriting-dashboard-page')
);

const UnderwritingSubmissionDetailPage = lazy(
  () => import('../pages/underwriting/underwriting-submission-detail-page')
);

const UnderwritingSubmissionDetailRiskProfilePage = lazy(
  () => import('../pages/underwriting/underwriting-risk-profile-page')
);

const UnderwritingSubmissionDetailRiskReportPage = lazy(
  () => import('../pages/underwriting/underwriting-risk-report-page')
);

const RaterPage = lazy(
  () => import('@converge/ui/domain/underwriting/rater/rater')
);

const UnderwritingQuotePage = lazy(
  () =>
    import(
      '../../../../libs/ui/src/domain/underwriting/quote/underwriting.quote.page'
    )
);

const UnderwritingBindPage = lazy(
  () =>
    import(
      '../../../../libs/ui/src/domain/underwriting/bind/underwriting.bind.page'
    )
);

const UnderwritingPolicyPage = lazy(
  () =>
    import(
      '../../../../libs/ui/src/domain/underwriting/policy/underwriting.policy.page'
    )
);

const UnderwritingQuotePackagePage = lazy(
  () => import('../pages/underwriting/underwriting.quote-package.page')
);

const ProfilePage = lazy(() => import('../pages/profile.page'));

const UnderwritingCreateSubmissionPage = lazy(
  () => import('../pages/underwriting/underwriting-create-submission.page')
);

// const CreateClaimPage = lazy(
//   () => import('../pages/entity/insured/insured-create-claim')
// );

const InsuredEntityPage = lazy(
  () => import('../pages/entity/insured/insured-entity-page')
);

const InsuredEntityDetailPage = lazy(
  () => import('../pages/entity/insured/insured-entity-detail-page')
);

const MidtermTransactionWizardPage = lazy(() =>
  import(
    '../../../../libs/ui/src/domain/entity/insured/midterm-transaction/midterm-transaction-wizard.page'
  ).then((m) => ({ default: m.MidtermTransactionWizardPage }))
);

const ClaimEntityDetailPage = lazy(
  () => import('../pages/entity/claims/claim-entity-detail-page')
);

const BrokerAgenciesEntityPage = lazy(
  () => import('../pages/entity/broker-agencies/broker-agencies-entity-page')
);

const QuotesEntityPage = lazy(
  () => import('../pages/entity/quotes/quotes-entity-page')
);

const PoliciesEntityPage = lazy(
  () => import('../pages/entity/policies/policies-entity-page')
);

const BrokerAgencyEntityDetailPage = lazy(
  () =>
    import('../pages/entity/broker-agencies/broker-agencies-entity-detail-page')
);

const ROUTES: RouteObject[] = [
  {
    path: '/',
    element: <Login />,
    errorElement: <ErrorBoundaryPage />,
  },
  {
    element: <AppLayout />,
    children: [
      {
        path: '/',
        element: <Navigate to="/underwriting" replace />,
      },
      {
        path: '/profile',
        element: <ProfilePage />,
      },

      {
        path: '/underwriting',
        element: <UnderwritingDashboardPage />,
      },
      {
        path: '/underwriting/create-submission',
        element: <UnderwritingCreateSubmissionPage />,
      },
      {
        path: '/underwriting/:submissionUuid',
        element: <UnderwritingSubmissionDetailPage />,
        children: [
          {
            // Redirect to overview
            index: true,
            path: '',
            element: <Navigate to="overview" />,
          },
          {
            index: true,
            path: 'overview',
            element: <UnderwritingSubmissionDetailOverview />,
          },
          {
            path: 'questionnaire/applications/:applicationId/instance/:submissionApplicationUuid/:subSectionId',
            element: <UnderwritingSubmissionDetailQuestionnaire />,
          },
          {
            path: `${UnderwritingSubmissionDetailPageSlug.RiskProfile}/risk-report`,
            element: <UnderwritingSubmissionDetailRiskReportPage />,
          },
          {
            path: UnderwritingSubmissionDetailPageSlug.RiskProfile,
            element: <UnderwritingSubmissionDetailRiskProfilePage />,
          },
          {
            path: `${UnderwritingSubmissionDetailPageSlug.Quote}/${RaterMode.CreateQuote}`,
            element: <RaterPage mode={RaterMode.CreateQuote} />,
          },
          {
            path: `${UnderwritingSubmissionDetailPageSlug.Quote}/${RaterMode.EditQuote}/:quoteUuid`,
            element: <RaterPage mode={RaterMode.EditQuote} />,
          },
          {
            path: UnderwritingSubmissionDetailPageSlug.Quote,
            element: <UnderwritingQuotePage />,
          },
          {
            path: `${UnderwritingSubmissionDetailPageSlug.Quote}/quote-package`,
            element: <UnderwritingQuotePackagePage />,
          },
          {
            path: UnderwritingSubmissionDetailPageSlug.Subjectivities,
            element: <UnderwritingSubjectivitiesPage />,
          },
          {
            path: UnderwritingSubmissionDetailPageSlug.Bind,
            element: <UnderwritingBindPage />,
          },
          {
            path: UnderwritingSubmissionDetailPageSlug.Policy,
            element: <UnderwritingPolicyPage />,
          },
          {
            path: '*',
            element: (
              <ErrorPageLayout
                message="Page not found"
                messageId="navigation.pageNotFound"
              />
            ),
          },
        ],
      },
      {
        path: '/insureds',
        element: <InsuredEntityPage />,
      },
      // {
      //   path: '/insureds/:insuredUuid/create-claim',
      //   element: <CreateClaimPage />,
      // },
      {
        path: '/insureds/:insuredUuid',
        element: <InsuredEntityDetailPage />,
        children: [
          {
            // Redirect to overview
            index: true,
            path: '',
            element: <Navigate to="overview" />,
          },
          {
            index: true,
            path: 'overview',
            element: <InsuredEntityDetailOverview />,
          },
          {
            index: true,
            path: 'policies',
            element: <InsuredPoliciesDetails />,
          },
          {
            index: true,
            path: 'reports',
            element: <InsuredEntityDetailReports />,
          },
          {
            index: true,
            path: 'documents',
            element: <InsuredDocumentDetails />,
          },
          {
            path: '*',
            element: (
              <ErrorPageLayout
                message="Page not found"
                messageId="navigation.pageNotFound"
              />
            ),
          },
        ],
      },
      {
        path: '/insureds/:insuredUuid/policies/:policyUuid/midterm-transaction',
        element: (
          <Suspense fallback={<CircularLoader />}>
            <MidtermTransactionWizardPage />
          </Suspense>
        ),
      },
      {
        path: '/broker-agencies',
        element: <BrokerAgenciesEntityPage />,
      },
      {
        path: '/broker-agencies/:brokerAgencyUuid',
        element: <BrokerAgencyEntityDetailPage />,
        children: [
          {
            // Redirect to overview
            index: true,
            path: '',
            element: <Navigate to="overview" />,
          },
          {
            index: true,
            path: 'overview',
            element: <BrokerAgencyDetailOverview />,
          },
          {
            path: 'agents',
            element: <BrokerAgencyDetailAgents />,
          },
          {
            path: '*',
            element: (
              <ErrorPageLayout
                message="Page not found"
                messageId="navigation.pageNotFound"
              />
            ),
          },
        ],
      },
      {
        path: '/policies',
        element: <PoliciesEntityPage />,
      },
      {
        path: '/policies/:policyUuid',
        element: <PolicyDetailPage />,
        children: [
          {
            // Redirect to overview
            index: true,
            path: '',
            element: <Navigate to="overview" />,
          },
          {
            index: true,
            path: 'overview',
            element: <PolicyDetailOverview />,
          },
          {
            path: '*',
            element: (
              <ErrorPageLayout
                message="Page not found"
                messageId="navigation.pageNotFound"
              />
            ),
          },
        ],
      },
      {
        path: '/claims',
        element: <ClaimEntityPage />,
      },
      {
        path: '/claims/:claimUuid',
        element: <ClaimEntityDetailPage />,
        children: [
          {
            // Redirect to overview
            index: true,
            path: '',
            element: <Navigate to="details" />,
          },
          {
            path: 'overview',
            element: <ClaimEntityDetailOverview />,
          },
          {
            index: true,
            path: 'details',
            element: <ClaimDetails />,
          },
          {
            path: 'coverage-letter',
            element: <ClaimEntityDetailCoverageLetter />,
          },
          {
            path: 'financials',
            element: <ClaimEntityDetailFinancials />,
          },
          {
            path: 'documents',
            element: <ClaimDocumentDetailNote />,
          },
          {
            path: 'notes',
            element: <ClaimEntityDetailNote />,
          },

          {
            path: 'contacts',
            element: <ClaimEntityDetailContact />,
          },
          {
            path: 'diary',
            element: <ClaimDiary />,
          },
          {
            path: 'reports',
            element: <ClaimReportDetail />,
          },
          {
            path: 'historyLog',
            element: <ClaimHistoryLogDetail />,
          },
          {
            path: '*',
            element: (
              <ErrorPageLayout
                message="Page not found"
                messageId="navigation.pageNotFound"
              />
            ),
          },
        ],
      },
      {
        path: '/producers/:producerType',
        element: <ProducersPage />,
      },
      {
        path: '/producers/broker/:brokerUuid',
        element: <Navigate to="details" replace />,
      },
      {
        path: '/producers/broker/:brokerUuid/details',
        element: <BrokerDetailPage />,
      },
      {
        path: '/producers/brokerage/:brokerageUuid',
        element: <Navigate to="details" replace />,
      },
      {
        path: '/producers/brokerage/:brokerageUuid/details',
        element: <BrokerageDetailPage />,
      },
      {
        path: '/producers/create-broker',
        element: <CreateBrokerContainer />,
      },
      {
        path: '/producers/create-brokerage',
        element: <BrokerageContainer />,
      },
      {
        path: '/quotes',
        element: <QuotesEntityPage />,
      },
      {
        path: '/quotes/:quoteUuid',
        element: <QuotesEntityDetailPage />,
        children: [
          {
            // Redirect to overview
            index: true,
            path: '',
            element: <Navigate to="overview" />,
          },
          {
            index: true,
            path: 'overview',
            element: <QuotesDetailOverview />,
          },
          {
            index: true,
            path: 'policies',
            element: <QuotesDetailPolicies />,
          },
          {
            index: true,
            path: 'submissions',
            element: <QuotesDetailSubmissions />,
          },
          {
            path: '*',
            element: (
              <ErrorPageLayout
                message="Page not found"
                messageId="navigation.pageNotFound"
              />
            ),
          },
        ],
      },
      {
        path: '*',
        element: (
          <main className="page-not-found">
            <Typography variant="h2" color="secondary">
              Page not found
            </Typography>
            <Box marginTop={1}>
              <Link to="/">
                <Button color="primary">Go home</Button>
              </Link>
            </Box>
          </main>
        ),
      },
    ],
    errorElement: <ErrorBoundaryPage />,
  },
];
export const ROUTER = createBrowserRouter(ROUTES);

export function App() {
  useAuthTokenRefresh();
  useIdleLogout();
  usePromptNavigationListener({ router: ROUTER });

  const {
    healthCheckError,
    healthCheckIsLoading,
    retryHealthCheck,
    healthCheckIsRetrying,
  } = useHealthCheckQuery();

  if (healthCheckError || healthCheckIsRetrying) {
    return (
      <Stack justifyContent={'center'} alignItems={'center'} flexGrow={1}>
        <ErrorPage
          id="health-check-error"
          message="Unable to connect to the Converge API"
          retry={() => retryHealthCheck()}
          retryLabel="Retry"
          isRetrying={healthCheckIsRetrying}
        />
      </Stack>
    );
  }

  if (healthCheckIsLoading) {
    return <CircularLoader className="circular-loader-style" />;
  }

  return (
    <Suspense fallback={<CircularLoader className="circular-loader-style" />}>
      <RouterProvider router={ROUTER} />
    </Suspense>
  );
}

export default App;
